import {
    CHART_DISPLAY_TYPE_ACCUMULATED_BAR,
    DashboardItemService,
    ITEM_TYPE_CHART,
    ITEM_TYPE_STATUS,
    ITEM_TYPE_TABLE,
    NormalizedVisConfig,
} from 'app/overview/dashboard/dashboard_item.service';
import {
    AccumulatedChartGraph,
    IMSDataVisChartData,
    TrendChartGraph,
} from 'app/shared/components/ims_data_vis/ims_data_chart.component';
import { IMSDataVisStatusData } from 'app/shared/components/ims_data_vis/ims_data_status.component';
import {
    IMSDataVisTableData,
    IMSDataVisTableRow,
} from 'app/shared/components/ims_data_vis/ims_data_table.component';
import { TimeZoneManager } from 'app/shared/time/TimeZoneManager';
import { formatUnix } from 'app/shared/utilities/time';
import { Format } from 'app/shared/utilities/TimestampFormatter';
import { flatten, toString, uniq } from 'lodash';
import { findDataSourceUnit } from './dashboard_item_settings_panel/dashboard_item_post_agg_helpers';

interface IMSQLDataForAccumulatedChart {
    unit: string;
    data: Array<{
        group_id: number;
        group_name: string;
        group_type: string;
        data: {
            [x: string]: number;
        };
    }>;
}

interface IMSQLDataForTrendChart {
    unit: string;
    data: Array<{
        group_id: number;
        group_name: string;
        group_type: string;
        data: Array<{
            timestamp: string;
            [x: string]: number | string;
        }>;
    }>;
}

export interface GroupDataItem {
    group_name: string;
    group_id: string | number;
    color?: string;
}
type GroupData = Array<GroupDataItem>;

type IMSQLDataForTable = Array<any>;

type IMSQLDataForStatusItem = {
    group_id: number;
    group_name: string;
    value: number | string;
    color?: {
        r?: number;
        g?: number;
        b?: number;
        a?: number;
    };
};

type IMSQLDataForStatusList = Array<IMSQLDataForStatusItem>;

/**
 * generates csv data in array format (array separates values)?
 * @param {IMSDataVisTableData} tableData
 * @return An array of array of strings
 * @example
 *  [
 *      ['asset_id', 'latency'],
 *      ['DT01', 1892],
 *      ['DT02', 13]
 *  ]
 */
function getTableDataAsCSV(tableData: IMSDataVisTableData): CSVDataAsArray {
    const headerRow = tableData.columns.map((column) => column.displayName);
    const valueRows = tableData.rows.map((row: IMSDataVisTableRow) =>
        tableData.columns.map((column) => toString(row.formattedValues[column.key])),
    );
    return [headerRow, ...valueRows];
}

/**
 * generates csv data in array format (array separates values)?
 */
function getStatusDataAsCSV(statusData: IMSDataVisStatusData): CSVDataAsArray {
    return [
        ['Name', 'Value'],
        ...statusData.collection.map((item) => [toString(item.groupName), toString(item.content)]),
    ];
}

/**
 * Formats a unix timestamp at the given timezone in iso8601
 * @param unixMs milliseconds since unix epoch
 * @param timeZone
 */
function formatUnixTimestamp(unixMs: number, timeZone = 'UTC'): string {
    if (!unixMs) {
        return 'Unknown';
    }
    return formatUnix(unixMs / 1000, timeZone, Format.ISO_8601_WITH_SPACE);
}

/**
 * generates csv data in array format (array separates values)?
 */

/**
 * Simple pure function, takes in chart data, returns CSV-able data.
 * Since the output is all just arrays of strings, we can pass in a timezone offset,
 * and this will be used to render the timestamps in that particular timezone offset.
 * The format of the date is fixed at ISO8601, but you can choose the timezone
 * @param chartData
 * @param timeZone The timezone to render datetime strings in.
 */
function getTrendChartDataAsCSV(
    chartData: IMSDataVisChartData<TrendChartGraph>,
    timeZone: string,
): CSVDataAsArray {
    if (!chartData.data) {
        return [];
    }
    const rows = chartData.data.reduce((rows, graph) => {
        const rowsForGroup = graph.values.reduce((rowsForGraph, value) => {
            rowsForGraph.push([
                toString(graph.key),
                formatUnixTimestamp(value.x, timeZone),
                toString(value.y),
            ]);
            return rowsForGraph;
        }, []);
        rows.push(...rowsForGroup);
        return rows;
    }, []);
    return [['Name', 'Timestamp', 'Value'], ...rows];
}

/**
 * generates csv data in array format (array separates values)?
 */
function getAccumulatedChartDataAsCSV(
    chartData: IMSDataVisChartData<AccumulatedChartGraph>,
    aggregationType: string,
): CSVDataAsArray {
    if (!chartData.data) {
        return [];
    }
    const allGroupIds = uniq(
        flatten(chartData.data.map((graph) => graph.values.map((group) => group.group_id))),
    );
    const allFields = chartData.data.map((graph) => graph.key);

    // try and turn the graph keys into better field names
    const formattedFieldNames = chartData.data.map((graph) => {
        const fieldName = DashboardItemService.fieldDetails.getIn([graph.rawKey, 'name']);
        const suffix = findDataSourceUnit(graph.rawKey, aggregationType);
        const aggType = getAggregationSuffix(aggregationType);
        return fieldName
            ? `${fieldName}${aggType}${suffix ? ` (${suffix.trim()})` : ''}`
            : graph.key;
    });

    // for each field find the matching graph it came from
    const graphsInFieldOrder = allFields.map((field) =>
        chartData.data.find((graph) => graph.key === field),
    );

    const rows = allGroupIds.map((groupId) => {
        // for each group (DT001) we need to produce an array like [DT001, 10, 20, 30] which matches the fields [Name, latency, apple, banana]
        // for each field, map it to only the corresponding value for the current group
        const valuesForFieldForCurrentGroup = graphsInFieldOrder.map((graph) =>
            graph.values.find((group) => group.group_id === groupId),
        );

        // for each data point, map it to the actual data point value (y property)
        const actualFieldValues = valuesForFieldForCurrentGroup.map((dataPoint) =>
            toString(dataPoint.y),
        );

        return [toString(valuesForFieldForCurrentGroup[0].x), ...actualFieldValues];
    });

    return [['Name', ...formattedFieldNames], ...rows];
}

interface AggregateDataItemStat {
    timestamp: string;
    [x: string]: number | string;
}

type AggregateDataItem = {
    group_id: number;
    group_name: string;
    data: Array<AggregateDataItemStat>;
};
type AggregateData = Array<AggregateDataItem>;

/**
 * Gives a string to use as a suffix when building a title for the given aggregation.
 * The return value will have a leading white space character or be an empty string if there's no
 * known aggregation.
 * @example const title = `CT001 Speed${getAggregationSuffix(aggType)}`
 * @param aggregationType
 */
export function getAggregationSuffix(aggregationType: string): string {
    return DashboardItemService.aggregationTypeOptions.getIn([aggregationType, 'suffix'], '');
}

export type AnyNetworkData =
    | AggregateData
    | IMSQLDataForStatusList
    | IMSQLDataForStatusItem
    | IMSQLDataForTable
    | IMSQLDataForTrendChart
    | IMSQLDataForAccumulatedChart;

type AnyVisData =
    | IMSDataVisTableData
    | IMSDataVisChartData<AccumulatedChartGraph | TrendChartGraph>
    | IMSDataVisStatusData;

type DataTransformerFn = (data: AnyNetworkData, visConfig: NormalizedVisConfig) => VisAndGroupData;

export interface VisAndGroupData<VisDataType = AnyVisData> {
    // data ready for the view components
    visualizationData: VisDataType;
    // normalized data ready for the legend
    groupData: GroupData;
}

export type CSVDataAsArray = Array<Array<string | number>>;

export class DataTransformer {
    private readonly _dataTransformerFn: DataTransformerFn;
    private readonly _timeZoneManager: TimeZoneManager;

    constructor(dataTransformerFn: DataTransformerFn, timeZoneManager: TimeZoneManager) {
        this._dataTransformerFn = dataTransformerFn;
        this._timeZoneManager = timeZoneManager;
    }

    transform(data: AnyNetworkData, visConfig: NormalizedVisConfig): VisAndGroupData {
        return this._dataTransformerFn(data, visConfig);
    }

    transformToCSV(data: AnyNetworkData, visConfig: NormalizedVisConfig): CSVDataAsArray {
        return toCSV(
            data,
            visConfig,
            this._dataTransformerFn,
            this._timeZoneManager.getTimeZone().string,
        );
    }
}

/**
 * Converts any type of dashboard data into it's CSV form.
 * @param data
 * @param visConfig
 * @param dataTransformerFn
 * @param timeZone The timezone to display all timestamps in
 */
function toCSV(
    data: AnyNetworkData,
    visConfig: NormalizedVisConfig,
    dataTransformerFn: DataTransformerFn,
    timeZone: string,
): CSVDataAsArray {
    const transformed = dataTransformerFn(data, visConfig);
    switch (visConfig.itemType) {
        case ITEM_TYPE_TABLE: {
            return getTableDataAsCSV(transformed.visualizationData as IMSDataVisTableData);
        }
        case ITEM_TYPE_STATUS: {
            return getStatusDataAsCSV(transformed.visualizationData as IMSDataVisStatusData);
        }
        case ITEM_TYPE_CHART: {
            if (visConfig.chartDisplayType === CHART_DISPLAY_TYPE_ACCUMULATED_BAR) {
                return getAccumulatedChartDataAsCSV(
                    transformed.visualizationData as IMSDataVisChartData<AccumulatedChartGraph>,
                    visConfig.aggregationType,
                );
            } else {
                return getTrendChartDataAsCSV(
                    transformed.visualizationData as IMSDataVisChartData<TrendChartGraph>,
                    timeZone,
                );
            }
        }
    }
}
